import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

type Props = {
  /**
   * Maps the current pathname to a short announcement. Return null to skip
   * the announcement for that route (e.g. on the initial mount).
   */
  describePath: (pathname: string) => string | null
}

/**
 * Aria-live region that announces SPA route changes to screen readers.
 *
 * Without this, AT users see no indication that they've navigated — the URL
 * changes, the DOM swaps, but they get no spoken cue. We deliberately render
 * the announcement only AFTER the initial mount so the page-load announcement
 * is left to the browser's normal title-change behaviour.
 */
export function RouteAnnouncer({ describePath }: Props) {
  const { pathname } = useLocation()
  const [message, setMessage] = useState('')
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true)
      return
    }
    const next = describePath(pathname)
    if (next) setMessage(next)
  }, [pathname, describePath, hasMounted])

  return (
    <div className="al-sr-only" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  )
}
